import classNames from "classnames";
import { ReactNode, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useParams } from "react-router-dom";
import { BackButton } from "../../../../../components/buttons/backButton";
import { Button } from "../../../../../components/buttons/button";
import { TagCard } from "../../../../../components/cards/tagCard";
import { MakeRepaymentModal } from "../../../../../components/modals/app_modals/loans/makeRepaymentModal";
import { LoanRepaymentScheduleModal } from "../../../../../components/modals/app_modals/loans/repaymentScheduleModal";
import { UploadLoanRepaymentModal } from "../../../../../components/modals/app_modals/loans/uploadRepaymentModal";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import {
  formatDate,
  formatToCurrency,
  formatToNumber,
} from "../../../../../helperFunctions";
import {
  useMemberFetchLoanDetailsQuery,
  useOfficialFetchLoanDetailsQuery,
} from "../../../../../redux/features/loans/loans.api";
import { FullLoanDetails } from "../../../../../redux/features/loans/loans.type";
import {
  loanrepaymentMethodMap,
  loanStatusMap,
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../../utils/constants";

export const ViewLoanDetailsPage = () => {
  const [fullLoanDetails, setFullLoanDetails] = useState<FullLoanDetails>();
  const [loanDetails, setLoanDetails] = useState<
    {
      title: ReactNode;
      element: ReactNode;
    }[]
  >([]);
  const [guarantorDetails, setGuarantorDetails] = useState<
    {
      title: ReactNode;
      element: ReactNode;
    }[]
  >([]);

  const { id, cooperativeId, type } = useParams<{
    id: string;
    cooperativeId: string;
    type: "member" | "official";
  }>();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: memberFullLoanDetails,
    isFetching: memberFetchingLoanDetails,
    refetch: memberRefetchLoanDetails,
  } = useMemberFetchLoanDetailsQuery(
    {
      loan_id: id!,
      detail: true,
      coop: cooperativeId!,
    },
    {
      skip: !id || !cooperativeId || type !== "member",
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: officialFullLoanDetails,
    isFetching: officialFetchingLoanDetails,
    refetch: officialRefetchLoanDetails,
  } = useOfficialFetchLoanDetailsQuery(
    {
      loan_id: id!,
      detail: true,
      coop: cooperativeId!,
    },
    {
      skip: !id || !cooperativeId || type !== "official",
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (type === "member") {
      setFullLoanDetails(memberFullLoanDetails?.data);
    } else {
      setFullLoanDetails(officialFullLoanDetails?.data);
    }
  }, [memberFullLoanDetails, officialFullLoanDetails, type]);

  useEffect(() => {
    setLoanDetails([
      {
        title: "First Name",
        element: fullLoanDetails?.MemberID?.User?.FirstName,
      },
      {
        title: "Middle Name",
        element: fullLoanDetails?.MemberID?.User?.MiddleName,
      },
      {
        title: "Last Name",
        element: fullLoanDetails?.MemberID?.User?.LastName,
      },
      {
        title: "Phone Number",
        element: `${fullLoanDetails?.MemberID?.User?.PhoneNumberCode}${fullLoanDetails?.MemberID?.User?.PhoneNumber}`,
      },
      {
        title: "Loan Type",
        element: fullLoanDetails?.LoanProduct?.LoanName,
      },
      {
        title: "Loan Amount",
        element: formatToCurrency(fullLoanDetails?.Amount ?? 0, {
          currency: "NGN",
          decimalPlaces: 2,
        }),
      },
      {
        title: "Loan Tenor:",
        element: `${formatToNumber(fullLoanDetails?.Tenure ?? 0, {})} months`,
      },
      {
        title: "Interest Rate",
        element: `${formatToNumber(
          fullLoanDetails?.LoanProduct?.MaxInterestRate ?? 0,
          {
            style: "percent",
          }
        )}`,
      },
      {
        title: "Repayment Method",
        element: fullLoanDetails?.RepaymentMode
          ? `${
              loanrepaymentMethodMap[fullLoanDetails?.RepaymentMode]?.shortText
            }` +
            (["card", "bank"].includes(fullLoanDetails.RepaymentMode) &&
            fullLoanDetails?.RepaymentMethodID?.last4
              ? ` ∙ ****${
                  fullLoanDetails.RepaymentMode === "card"
                    ? fullLoanDetails?.RepaymentMethodID?.last4
                    : fullLoanDetails?.RepaymentMethodID?.AccountNumber
                } (${
                  fullLoanDetails.RepaymentMode === "card"
                    ? fullLoanDetails?.RepaymentMethodID?.card_type
                    : fullLoanDetails?.RepaymentMethodID?.BankName
                })`
              : "")
          : "N/A",
      },
      {
        title: "Status",
        element: (
          <TagCard
            text={loanStatusMap[fullLoanDetails?.Status ?? "in-progress"].text}
            type={
              loanStatusMap[fullLoanDetails?.Status ?? "in-progress"].tagVariant
            }
          />
        ),
      },
      {
        title: "Date Requested",
        element: formatDate(fullLoanDetails?.DateAdded),
      },
      {
        title: "Repayment Schedule",
        element: (
          <Button
            className="!py-0 !px-0"
            type="button"
            onClick={() => {
              handleModalOpen("viewRepaymentScheduleModal");
            }}
            text="View Schedule"
            variation="plain"
          />
        ),
      },
    ]);

    setGuarantorDetails([
      {
        title: "Number of Guarantors",
        element: formatToNumber(fullLoanDetails?.NoOfGuarantor ?? 0, {}),
      },
      ...(fullLoanDetails?.Guarantor ?? []).flatMap((guarantor, idx) => {
        return [
          {
            title: `Guarantor ${idx + 1}`,
            element: `${guarantor?.GuarantorMemberID?.User?.FirstName} ${guarantor?.GuarantorMemberID?.User?.LastName}`,
          },
          {
            title: "Amount Guaranteed",
            element: formatToCurrency(guarantor?.AmountGuarantee ?? 0, {
              currency: "NGN",
              decimalPlaces: 2,
            }),
          },
        ];
      }),
    ]);
    // eslint-disable-next-line
  }, [fullLoanDetails]);

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <BackButton text="Back" className="max-md:hidden" />

          {type === "member" ? (
            <div className="grid grid-cols-2 items-center gap-2 w-full md:w-fit">
              <Button
                type="button"
                text="Upload Repayment"
                variation="plain-2"
                size="medium"
                onClick={() => {
                  handleModalOpen("uploadLoanRepaymentModal");
                }}
                className={classNames({
                  hidden: fullLoanDetails?.Status !== "active",
                })}
              />

              <Button
                type="button"
                text="Make Repayment"
                variation="primary"
                size="medium"
                onClick={() => {
                  handleModalOpen("makeRepaymentModal");
                }}
                className={classNames({
                  hidden: fullLoanDetails?.Status !== "active",
                })}
              />
            </div>
          ) : (
            <div></div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 rounded-lg overflow-hidden">
          <div className="flex flex-col gap-8 bg-white p-4 md:p-10">
            <h1 className="font-bold text-xl md:text-2xl">Loan Details</h1>

            <div>
              {memberFetchingLoanDetails || officialFetchingLoanDetails
                ? Array.from({ length: 10 }).map((_, idx) => {
                    return (
                      <div
                        key={`loan-details-${idx}`}
                        className="py-3 first:pt-0 last:pb-0 border-b last:border-b-0"
                      >
                        <Skeleton
                          width={"100%"}
                          height={24}
                          baseColor={SKELETON_BASE_COLOR}
                          highlightColor={SKELETON_HIGHLIGHT_COLOR}
                          className="rounded-lg"
                          containerClassName="w-full"
                        />
                      </div>
                    );
                  })
                : loanDetails.map((loanDetail, idx) => {
                    return (
                      <div
                        key={`loan-details-${idx}`}
                        className="py-3 gap-3 flex items-center justify-between first:pt-0 last:pb-0 border-b last:border-b-0 text-sm md:text-base text-pry-dark-green-400/80"
                      >
                        <div className="font-medium">{loanDetail.title}:</div>
                        <div className="text-right">{loanDetail.element}</div>
                      </div>
                    );
                  })}
            </div>
          </div>

          <div className="flex flex-col gap-6 bg-light-green-bg-200 p-4 md:p-10">
            <p className="font-bold text-lg md:text-xl mt-2">
              Guarantors Information
            </p>

            <div className="flex flex-col gap-4">
              {memberFetchingLoanDetails || officialFetchingLoanDetails
                ? Array.from({ length: 6 }).map((_, idx) => {
                    return (
                      <div key={`loan-details-${idx}`} className="">
                        <Skeleton
                          width={"100%"}
                          height={24}
                          baseColor={SKELETON_BASE_COLOR}
                          highlightColor={SKELETON_HIGHLIGHT_COLOR}
                          className="rounded-lg"
                          containerClassName="w-full"
                        />
                      </div>
                    );
                  })
                : guarantorDetails.map((guarantorDetail, idx) => {
                    return (
                      <div
                        key={`guarantor-details-${idx}`}
                        className="flex items-center justify-between text-sm md:text-base text-pry-dark-green-400/80"
                      >
                        <div className="font-medium text-pry-dark-green-400">
                          {guarantorDetail.title}:
                        </div>
                        <div>{guarantorDetail.element}</div>
                      </div>
                    );
                  })}
            </div>
          </div>
        </div>
      </div>

      {fullLoanDetails ? (
        <>
          <LoanRepaymentScheduleModal
            isModalOpen={modalOpen && modal === "viewRepaymentScheduleModal"}
            onModalClose={() => {
              handleModalClose();
            }}
            loanDetails={fullLoanDetails}
            type={type ?? "member"}
          />

          {type === "member" ? (
            <>
              <MakeRepaymentModal
                modal={modal}
                modalOpen={modalOpen}
                handleModalClose={() => {
                  handleModalClose();
                }}
                handleModalOpen={handleModalOpen}
                loanDetails={fullLoanDetails}
                onSuccess={async () => {
                  await memberRefetchLoanDetails();
                  await officialRefetchLoanDetails();
                }}
              />

              <UploadLoanRepaymentModal
                modal={modal}
                modalOpen={modalOpen}
                handleModalClose={() => {
                  handleModalClose();
                }}
                handleModalOpen={handleModalOpen}
                loanDetails={fullLoanDetails}
                onSuccess={async () => {}}
              />
            </>
          ) : undefined}
        </>
      ) : undefined}
    </>
  );
};
